// src/worldCup2026Scoring.js
//
// FIFA World Cup 2026 — Live scoring & subscription helpers
//
// ⚠️ RECONSTRUCTED FILE — read this before editing.
//
// The original lived only on the Mac used for production builds and was
// never committed to git. Reconstructed on 2026-05-11 by reading the
// consumer contracts in TorchApp.jsx and WorldCupBracket.jsx. Behavior
// is best-effort but functionally complete for pre-tournament use.
//
// Builds on: worldCup2026Scoring-wc-cleanup-20260526.js
// Adds:      computeKnockoutScore, hasAnyKnockoutData, ROUND_POINTS_PER_WIN
//            re-export. Extends the live standings doc shape with a
//            `knockouts` section populated by the cron Cloud Function.
//
// Seven exports, all consumed by the app:
//   • subscribeToWorldCupLiveStandings(cb) → unsubscribe fn
//   • useWorldCupLiveStandings()           → standings | null
//   • computeGroupStageScore(predictions, standings) → group score result
//   • computeKnockoutScore(predictions, standings)   → knockout score result
//   • hasAnyLiveData(standings)            → bool, group-stage activity
//   • hasAnyKnockoutData(standings)        → bool, knockout activity
//   • formatLastUpdatedAgo(ts)             → string
//
// ── Cloud Function contract ────────────────────────────────────────────
//
// The cron Cloud Function (lives in functions/) writes a single doc at
//   /worldCup2026Live/standings
// at ~5 min cadence during the tournament (June 11 – July 19, 2026).
// Pre-tournament the doc may not exist; subscribers receive null.
//
// Expected doc shape:
//
//   {
//     // Group stage standings (1st-4th by FIFA tiebreakers).
//     groups: {
//       A: {
//         standings: ["MEX", "KOR", "RSA", "CZE"],
//         matchesPlayed: 6,        // matches played so far in this group
//         isComplete: true,        // group stage finished for this group
//       },
//       B: { standings: [...], matchesPlayed: 0 },
//       ...L
//     },
//
//     // Knockout state. Populated round-by-round as fixtures resolve.
//     knockouts: {
//       R32: [
//         {
//           matchSlot: "R32-1",     // matches KNOCKOUT_MATCHUPS slot IDs
//           homeCode:  "MEX",       // resolved home team code (3 letters)
//           awayCode:  "QAT",       // resolved away team code
//           homeScore: 2,           // optional — current/final score
//           awayScore: 1,           // optional
//           winnerCode: "MEX",      // null until decided
//           decidedBy: "regulation" | "extra_time" | "penalties", // optional
//           completedAt: <Timestamp | ISO | ms>,                  // optional
//         },
//         ...up to 16 entries
//       ],
//       R16: [...up to 8],
//       QF:  [...up to 4],
//       SF:  [...up to 2],
//       FINAL: [...up to 1],
//
//       // Third-place assignments. Populated once the group stage ends
//       // and FIFA's tiebreakers determine the 8 advancing third-placers.
//       // Keyed by the R32 slot that has a third-place cluster on its
//       // away side. Exactly 8 keys once populated.
//       thirdPlaceAssignments: {
//         "R32-2": "QAT", "R32-5": "...", "R32-7": "...",
//         "R32-8": "...", "R32-9": "...", "R32-10": "...",
//         "R32-13": "...", "R32-15": "...",
//       },
//     },
//
//     lastUpdated: <Firestore Timestamp | ISO string | ms number>,
//   }
//
// Backwards-tolerant shapes also accepted on the group-stage side:
//   • groups[L] as a flat array of codes (matchesPlayed assumed 0)
//   • missing matchesPlayed (treated as 0)
//   • missing isComplete    (treated as false)
//
// Per Tom's decision: PK/extra-time winner counts as the bracket winner.
// Scoring code only reads `winnerCode`; it doesn't distinguish how the
// match was decided.

import { useEffect, useState } from "react";
import app from "./firebase";
import {
  GROUP_LETTERS,
  KNOCKOUT_ROUNDS,
  ROUND_POINTS_PER_WIN,
  SCORING,
} from "./worldCup2026Config";

// ── Internal helpers ──────────────────────────────────────────────────

// Normalize a group entry to { standings: string[], matchesPlayed, isComplete }.
// Returns null if the shape is unusable.
function normalizeGroup(raw) {
  if (!raw) return null;
  let standings = null;
  let matchesPlayed = 0;
  let isComplete = false;

  if (Array.isArray(raw)) {
    standings = raw;
  } else if (Array.isArray(raw.standings)) {
    standings = raw.standings;
    matchesPlayed = Number(raw.matchesPlayed) || 0;
    isComplete = !!raw.isComplete;
  } else {
    return null;
  }

  // Filter out null/undefined entries; preserve order.
  standings = standings.filter((c) => typeof c === "string" && c.length > 0);
  if (standings.length === 0) return null;
  return { standings, matchesPlayed, isComplete };
}

// ── Public API ────────────────────────────────────────────────────────

/**
 * Subscribe to the live World Cup standings doc. Callback receives the
 * full doc data on each snapshot (or null if the doc doesn't exist yet).
 *
 * Returns an unsubscribe function. Safe to call before Firebase is
 * fully initialized — uses the async-import pattern with a cancellation
 * flag so calling the returned unsub before the inner subscription is
 * set up still works correctly.
 *
 * @param {(standings: object | null) => void} cb
 * @returns {() => void} unsubscribe
 */
export function subscribeToWorldCupLiveStandings(cb) {
  if (typeof cb !== "function") return () => {};

  let cancelled = false;
  let innerUnsub = null;

  (async () => {
    try {
      const { getFirestore, doc, onSnapshot } =
        await import("firebase/firestore");
      if (cancelled) return;
      const fdb = getFirestore(app);
      const ref = doc(fdb, "worldCup2026Live", "standings");
      innerUnsub = onSnapshot(
        ref,
        (snap) => {
          if (cancelled) return;
          cb(snap.exists() ? (snap.data() || null) : null);
        },
        (err) => {
          console.error("[WC2026] live standings subscription error:", err);
          if (!cancelled) cb(null);
        }
      );
    } catch (e) {
      console.error("[WC2026] failed to subscribe to live standings:", e);
      if (!cancelled) cb(null);
    }
  })();

  return () => {
    cancelled = true;
    if (innerUnsub) {
      try { innerUnsub(); } catch (_e) { /* no-op */ }
    }
  };
}

/**
 * React hook for the live World Cup standings doc.
 *
 * Returns:
 *   • null pre-tournament (doc doesn't exist or hasn't loaded yet)
 *   • the full doc data once Firestore returns it
 *
 * Consumer pattern:
 *   const standings = useWorldCupLiveStandings();
 *   const groupScore = computeGroupStageScore(predictions, standings);
 *   const koScore    = computeKnockoutScore(predictions, standings);
 *   const showLive   = hasAnyLiveData(standings);
 */
export function useWorldCupLiveStandings() {
  const [standings, setStandings] = useState(null);
  useEffect(() => {
    const unsub = subscribeToWorldCupLiveStandings(setStandings);
    return () => {
      try { unsub(); } catch (_e) { /* no-op */ }
    };
  }, []);
  return standings;
}

/**
 * True if there's enough live data to surface the "Live" UI.
 * Specifically: at least one group has at least one match played.
 *
 * Pre-tournament returns false (the live HUD stays hidden).
 *
 * @param {object | null} standings
 * @returns {boolean}
 */
export function hasAnyLiveData(standings) {
  if (!standings || !standings.groups) return false;
  for (const L of GROUP_LETTERS) {
    const g = normalizeGroup(standings.groups[L]);
    if (g && g.matchesPlayed > 0) return true;
  }
  return false;
}

/**
 * True if any knockout activity has been recorded — either at least one
 * knockout match has been written into the standings doc, or the eight
 * third-place advancement slots have been assigned (i.e. group stage
 * concluded and FIFA's tiebreakers have produced the third-place ladder).
 *
 * Used by the bracket UI to decide whether to show the "Waiting for
 * group stage to finish" empty state vs real knockout matchup cards.
 *
 * @param {object | null} standings
 * @returns {boolean}
 */
export function hasAnyKnockoutData(standings) {
  const knockouts = standings?.knockouts;
  if (!knockouts || typeof knockouts !== "object") return false;

  // Any round with at least one written match (even pre-completion).
  for (const round of KNOCKOUT_ROUNDS) {
    const arr = knockouts[round];
    if (Array.isArray(arr)) {
      for (const m of arr) {
        if (m && (m.matchSlot || m.homeCode || m.awayCode || m.winnerCode)) {
          return true;
        }
      }
    }
  }

  // Third-place assignments populated → group stage concluded.
  const assignments = knockouts.thirdPlaceAssignments;
  if (assignments && typeof assignments === "object" && Object.keys(assignments).length > 0) {
    return true;
  }

  return false;
}

/**
 * Score a member's group-stage predictions against live standings.
 *
 * Per-group scoring: each correctly-placed team (1st-4th) earns
 * SCORING.GROUP_STAGE_PER_SLOT points. Max 4 × 12 × 5 = 240 pts.
 *
 * Returns a result object whose shape matches what WorldCupBracket and
 * TorchApp expect:
 *   {
 *     totalPoints: number,        // sum across all groups
 *     matchesPlayed: number,      // sum across all groups
 *     isFullyComplete: bool,      // every group has isComplete: true
 *     groups: {
 *       A: {
 *         hasPrediction: bool,
 *         slotResults: [
 *           { predictedCode, actualCode, isCorrect }, ...4 entries
 *         ],
 *         correctSlots: number,   // 0-4
 *         points: number,         // correctSlots * GROUP_STAGE_PER_SLOT
 *         isComplete: bool,       // group stage finished for this group
 *       },
 *       B: { ... }, ...L
 *     }
 *   }
 *
 * Pre-tournament (or with no live data): returns zero points but still
 * provides the per-group container so consumers don't have to null-check
 * everything.
 *
 * @param {object} predictions   { groups: { A: [code, code, code, code], ... } }
 * @param {object | null} standings
 */
export function computeGroupStageScore(predictions, standings) {
  const result = {
    totalPoints: 0,
    matchesPlayed: 0,
    isFullyComplete: false,
    groups: {},
  };

  // Pre-tournament: no standings doc yet. Return zero with empty per-group
  // entries so the bracket UI can still render predictions side without
  // null-checking the result.
  if (!standings || !standings.groups) {
    for (const L of GROUP_LETTERS) {
      const predicted = Array.isArray(predictions?.groups?.[L])
        ? predictions.groups[L].slice(0, 4)
        : null;
      result.groups[L] = {
        hasPrediction: !!(predicted && predicted.length),
        slotResults: [],
        correctSlots: 0,
        points: 0,
        isComplete: false,
      };
    }
    return result;
  }

  let completeGroupCount = 0;

  for (const L of GROUP_LETTERS) {
    const actual = normalizeGroup(standings.groups[L]);
    const predicted = Array.isArray(predictions?.groups?.[L])
      ? predictions.groups[L].slice(0, 4)
      : null;
    const hasPrediction = !!(predicted && predicted.length);

    if (!actual) {
      result.groups[L] = {
        hasPrediction,
        slotResults: [],
        correctSlots: 0,
        points: 0,
        isComplete: false,
      };
      continue;
    }

    result.matchesPlayed += actual.matchesPlayed;
    if (actual.isComplete) completeGroupCount += 1;

    if (!hasPrediction) {
      // No prediction for this group — surface live state for the header
      // pill but award no points.
      result.groups[L] = {
        hasPrediction: false,
        slotResults: [],
        correctSlots: 0,
        points: 0,
        isComplete: actual.isComplete,
      };
      continue;
    }

    // Per-slot correctness: predicted[i] vs actual.standings[i].
    const slotResults = [];
    let correctSlots = 0;
    for (let i = 0; i < 4; i++) {
      const predictedCode = predicted[i] ?? null;
      const actualCode = actual.standings[i] ?? null;
      const isCorrect = !!(predictedCode && actualCode && predictedCode === actualCode);
      if (isCorrect) correctSlots += 1;
      slotResults.push({ predictedCode, actualCode, isCorrect });
    }
    const points = correctSlots * SCORING.GROUP_STAGE_PER_SLOT;
    result.totalPoints += points;

    result.groups[L] = {
      hasPrediction: true,
      slotResults,
      correctSlots,
      points,
      isComplete: actual.isComplete,
    };
  }

  result.isFullyComplete = completeGroupCount === GROUP_LETTERS.length;
  return result;
}

/**
 * Score a member's knockout predictions against live standings.
 *
 * Per-round scoring (from SCORING constants):
 *   R32:   5 pts × 16 =  80 max
 *   R16:  15 pts ×  8 = 120 max
 *   QF:   30 pts ×  4 = 120 max
 *   SF:   60 pts ×  2 = 120 max
 *   FINAL: 200 pts × 1 = 200 max  (uses SCORING.CHAMPION)
 *   Knockout total max: 640 pts.
 *
 * Award rule: for each *completed* knockout match (winnerCode present in
 * the live doc), if the user's pick equals the recorded winner, award
 * that round's points. Otherwise zero. Tom's decision: PK/AET winners
 * count as bracket winners, so this code reads winnerCode at face value
 * regardless of how the match was decided.
 *
 * Returns:
 *   {
 *     totalPoints: number,
 *     matchesCompleted: number,           // across all rounds
 *     isFullyComplete: bool,              // every round populated and decided
 *     rounds: {
 *       R32: {
 *         pointsPerWin:    5,
 *         points:          number,        // sum of points earned in this round
 *         correctPicks:    number,        // 0..completedMatches
 *         completedMatches: number,       // matches with winnerCode set
 *         totalMatches:    16,            // KNOCKOUT_SLOTS[round]
 *         hasAnyPicks:     bool,          // did the user pick at least 1 in this round
 *         picks: [
 *           {
 *             matchSlot:    string,
 *             predictedCode: string | null,
 *             winnerCode:    string,
 *             homeCode:      string | null,
 *             awayCode:      string | null,
 *             isCorrect:     bool,
 *             pointsEarned:  number,
 *           },
 *           ...
 *         ],
 *       },
 *       R16: {...}, QF: {...}, SF: {...}, FINAL: {...},
 *     },
 *   }
 *
 * Pre-data: rounds are still keyed (with zeros and empty pick arrays)
 * so consumers can iterate without null-checking.
 *
 * @param {object} predictions   { knockouts: { R32: {matchSlot: code}, R16: {...}, ... } }
 * @param {object | null} standings
 */
export function computeKnockoutScore(predictions, standings) {
  const ROUND_TOTALS = {
    R32: 16, R16: 8, QF: 4, SF: 2, FINAL: 1,
  };

  const makeRound = (round) => ({
    pointsPerWin:     ROUND_POINTS_PER_WIN[round] ?? 0,
    points:           0,
    correctPicks:     0,
    completedMatches: 0,
    totalMatches:     ROUND_TOTALS[round] ?? 0,
    hasAnyPicks:      false,
    picks:            [],
  });

  const result = {
    totalPoints:      0,
    matchesCompleted: 0,
    isFullyComplete:  false,
    rounds: {
      R32:   makeRound("R32"),
      R16:   makeRound("R16"),
      QF:    makeRound("QF"),
      SF:    makeRound("SF"),
      FINAL: makeRound("FINAL"),
    },
  };

  // Surface hasAnyPicks even when there's no live data yet, so the UI
  // can show "you've made N picks" pre-tournament. Predictions are
  // organized as predictions.knockouts[round] = { matchSlot: teamCode }.
  for (const round of KNOCKOUT_ROUNDS) {
    const picksObj = predictions?.knockouts?.[round];
    if (picksObj && typeof picksObj === "object") {
      for (const v of Object.values(picksObj)) {
        if (typeof v === "string" && v.length > 0) {
          result.rounds[round].hasAnyPicks = true;
          break;
        }
      }
    }
  }

  // Pre-data: no knockouts written yet. Return well-formed zero result.
  const knockouts = standings?.knockouts;
  if (!knockouts || typeof knockouts !== "object") {
    return result;
  }

  let roundsFullyDecided = 0;

  for (const round of KNOCKOUT_ROUNDS) {
    const roundBucket = result.rounds[round];
    const pointsPerWin = roundBucket.pointsPerWin;
    const userPicks = predictions?.knockouts?.[round] ?? {};
    const roundArr = Array.isArray(knockouts[round]) ? knockouts[round] : [];

    for (const match of roundArr) {
      if (!match || typeof match !== "object") continue;
      const matchSlot = match.matchSlot;
      if (!matchSlot || typeof matchSlot !== "string") continue;
      const winnerCode = typeof match.winnerCode === "string" && match.winnerCode.length > 0
        ? match.winnerCode
        : null;

      // We only score *completed* matches. Pending matches (winner not yet
      // recorded) don't show up in the picks array; the UI sources its
      // upcoming-matchups view from KNOCKOUT_MATCHUPS + live standings via
      // getMatchTeams, not from this scoring result.
      if (!winnerCode) continue;

      roundBucket.completedMatches += 1;
      result.matchesCompleted += 1;

      const predictedCode = typeof userPicks[matchSlot] === "string" && userPicks[matchSlot].length > 0
        ? userPicks[matchSlot]
        : null;
      const isCorrect = predictedCode !== null && predictedCode === winnerCode;
      const pointsEarned = isCorrect ? pointsPerWin : 0;

      if (isCorrect) {
        roundBucket.correctPicks += 1;
        roundBucket.points += pointsEarned;
        result.totalPoints += pointsEarned;
      }

      roundBucket.picks.push({
        matchSlot,
        predictedCode,
        winnerCode,
        homeCode: typeof match.homeCode === "string" ? match.homeCode : null,
        awayCode: typeof match.awayCode === "string" ? match.awayCode : null,
        isCorrect,
        pointsEarned,
      });
    }

    if (roundBucket.completedMatches === roundBucket.totalMatches && roundBucket.totalMatches > 0) {
      roundsFullyDecided += 1;
    }
  }

  result.isFullyComplete = roundsFullyDecided === KNOCKOUT_ROUNDS.length;
  return result;
}

/**
 * Score a member's Golden Boot prediction.
 *
 * The Golden Boot (tournament top scorer) has no live data feed, so the
 * winning player is set by an admin AFTER the tournament ends, written to
 * the standings doc as a top-level `goldenBootCode` string (the player's
 * name). The cron never touches this field.
 *
 * Matching is name-based and lenient: both the user's pick and the admin's
 * answer are lowercased and whitespace-collapsed before comparison, so
 * "Kylian Mbappé " and "kylian mbappe" match. (Diacritics are NOT stripped
 * here — admins should enter the name as it appears in the pick list.)
 *
 * Returns:
 *   {
 *     awarded:   bool,    // true once an admin has set goldenBootCode
 *     isCorrect: bool,    // user's pick matches the winner
 *     points:    number,  // SCORING.GOLDEN_BOOT or 0
 *     pickName:  string|null,
 *     winnerName: string|null,
 *   }
 */
export function computeGoldenBootScore(predictions, standings) {
  const norm = (s) =>
    typeof s === "string" ? s.toLowerCase().replace(/\s+/g, " ").trim() : "";

  const pickName = typeof predictions?.goldenBoot === "string" && predictions.goldenBoot.trim()
    ? predictions.goldenBoot.trim()
    : null;
  const winnerName = typeof standings?.goldenBootCode === "string" && standings.goldenBootCode.trim()
    ? standings.goldenBootCode.trim()
    : null;

  // Not yet decided by an admin → nothing awarded, scoring stays open.
  if (!winnerName) {
    return { awarded: false, isCorrect: false, points: 0, pickName, winnerName: null };
  }

  const isCorrect = !!pickName && norm(pickName) === norm(winnerName);
  return {
    awarded: true,
    isCorrect,
    points: isCorrect ? SCORING.GOLDEN_BOOT : 0,
    pickName,
    winnerName,
  };
}

/**
 * Format a relative "time ago" string for the "last updated" label.
 *
 * Accepts: Firestore Timestamp (with .toDate or .seconds), Date, ISO
 * string, or milliseconds number. Returns "" for invalid input.
 *
 *   formatLastUpdatedAgo(Date.now())         // → "just now"
 *   formatLastUpdatedAgo(Date.now() - 90e3)  // → "1m ago"
 *   formatLastUpdatedAgo(Date.now() - 7e6)   // → "1h ago"
 *
 * @param {*} ts
 * @returns {string}
 */
export function formatLastUpdatedAgo(ts) {
  if (!ts) return "";
  let d;
  if (typeof ts.toDate === "function") {
    d = ts.toDate();
  } else if (ts instanceof Date) {
    d = ts;
  } else if (typeof ts === "string") {
    d = new Date(ts);
  } else if (typeof ts === "number") {
    d = new Date(ts);
  } else if (typeof ts.seconds === "number") {
    d = new Date(ts.seconds * 1000);
  } else {
    return "";
  }
  if (Number.isNaN(d.getTime())) return "";

  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return diffMin === 1 ? "1m ago" : `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return diffHr === 1 ? "1h ago" : `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return diffDay === 1 ? "1d ago" : `${diffDay}d ago`;
}
